import { create } from 'zustand';
import apiClient from '../api/api';
import toast from 'react-hot-toast';
import { useAuthStore } from './useAuthStore';

const initialState = {
  // ====== FRIENDS ======
  onlineUsers: [],
  friends: [],
  friendRequests: [],
  sentRequests: [],
  pendingOnlineUsers: [],
  retryCount: 0,
  maxRetries: 3,
  lastError: null,

  // ====== NOTIFICATIONS ======
  notifications: [],
  unreadCount: 0,
  isNotificationsLoading: false,

  // ====== BLOCKED USERS ======
  blockedUsers: [],
  blockedByUsers: [],

  // ====== ACTIVITY ======
  activityLog: [],
  activityCount: 0,

  // ====== PRIVACY OVERLAY ======
  showPrivateOverlay: false,

  // ====== SUPPORT ======
  supportTickets: [],
  supportUnreadCount: 0,

  // ====== LOADING ======
  isLoading: true,
};

export const useInteractionStore = create((set, get) => ({
  ...initialState,

  // =========================
  // FRIENDS ACTIONS
  // =========================

  togglePrivateOverlay: (nextValue) => {
    set((state) => ({
      showPrivateOverlay: typeof nextValue === 'boolean' ? nextValue : !state.showPrivateOverlay,
    }));
  },

  applyOnlineStatus: (friends, onlineUserIds) => {
    if (!friends || friends.length === 0) return friends;
    
    return friends.map((friend) => {
      if (!friend || !friend._id) return friend;
      
      const isOnline = onlineUserIds.includes(friend._id.toString());
      
      // Optimización: Si el estado no cambia, devolvemos el mismo objeto
      if (friend.isOnline === isOnline) return friend;
      
      return { ...friend, isOnline };
    });
  },

  fetchFriendsData: async () => {
    // 🔴 GUARD: No llamar si no hay usuario autenticado
    const authState = useAuthStore.getState();
    if (!authState.isLoggedIn || !authState.currentUser) {
      return;
    }
    
    set({ isLoading: true, lastError: null });
    
    const normalizeArray = (arr) => (arr || []).filter(item => item && item._id);
    
    const executeWithRetry = async (attempt = 0) => {
      try {
        const response = await apiClient.get('/users/friends');
        const newFriends = normalizeArray(response.data.friends);
        const newFriendRequests = normalizeArray(response.data.friendRequests);
        const newSentRequests = normalizeArray(response.data.sentRequests);
        
        const currentState = get();
        const onlineUsersToApply = currentState.pendingOnlineUsers.length > 0 
          ? currentState.pendingOnlineUsers 
          : currentState.onlineUsers;
        
        const friendsWithOnlineStatus = get().applyOnlineStatus(newFriends, onlineUsersToApply);
        
        set({
          friends: friendsWithOnlineStatus,
          friendRequests: newFriendRequests,
          sentRequests: newSentRequests,
          isLoading: false,
          pendingOnlineUsers: [],
          retryCount: 0,
          lastError: null,
        });
        
        return true;
      } catch (error) {
        const isNetworkError = !error.response || error.response.status >= 500 || error.code === 'ECONNABORTED';
        const shouldRetry = isNetworkError && attempt < get().maxRetries;
        
        if (shouldRetry) {
          const delayMs = Math.min(1000 * Math.pow(2, attempt), 5000);
          
          set({ retryCount: attempt + 1 });
          await new Promise(resolve => setTimeout(resolve, delayMs));
          
          return executeWithRetry(attempt + 1);
        } else {
          // 🔴 SILENCIO: Solo errores críticos
          if (import.meta.env.DEV) {
            console.error('Error al cargar amigos:', error.message);
          }
          set({ 
            friends: [], 
            friendRequests: [], 
            sentRequests: [], 
            isLoading: false,
            lastError: error.message,
          });
          return false;
        }
      }
    };

    return executeWithRetry();
  },

  updateOnlineUsers: (userIds) => {
    const currentFriends = get().friends;
    
    if (currentFriends.length === 0) {
      set({ pendingOnlineUsers: userIds });
      return;
    }
    
    const friendsWithOnlineStatus = get().applyOnlineStatus(currentFriends, userIds);
    set({ 
      onlineUsers: userIds,
      friends: friendsWithOnlineStatus,
      pendingOnlineUsers: [],
    });
  },

  handleUserOnline: (userId) => {
    const currentState = get();
    const isAlreadyOnline = currentState.onlineUsers.includes(userId);
    
    if (!isAlreadyOnline) {
      const updatedOnlineUsers = [...currentState.onlineUsers, userId];
      get().updateOnlineUsers(updatedOnlineUsers);
    }
  },

  handleUserOffline: (userId) => {
    const currentState = get();
    const updatedOnlineUsers = currentState.onlineUsers.filter(id => id !== userId);
    get().updateOnlineUsers(updatedOnlineUsers);
  },

  sendFriendRequest: async (userId) => {
    try {
      const response = await apiClient.post(`/users/add-friend/${userId}`, { type: 'friend' });
      set((state) => ({
        sentRequests: state.sentRequests.some((request) => request?._id === userId)
          ? state.sentRequests
          : [...state.sentRequests, { _id: userId }],
      }));
      toast.success('Solicitud enviada');
      return { success: true };
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error al enviar solicitud');
      return { success: false };
    }
  },

  acceptFriendRequest: async (requesterId) => {
    try {
      await apiClient.post(`/users/accept-friend/${requesterId}`);

      set((state) => {
        const updatedRequests = state.friendRequests.filter(r => r._id !== requesterId);
        const acceptedFriend = state.friendRequests.find(r => r._id === requesterId);
        return {
          friendRequests: updatedRequests,
          sentRequests: state.sentRequests.filter(r => r._id !== requesterId),
          friends: acceptedFriend ? [...state.friends, acceptedFriend] : state.friends,
        };
      });

      toast.success('Solicitud aceptada');
      return { success: true };
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error al aceptar solicitud');
      return { success: false };
    }
  },

  rejectFriendRequest: async (requesterId) => {
    try {
      await apiClient.delete(`/users/remove-friend/${requesterId}`);
      set((state) => ({
        friendRequests: state.friendRequests.filter((request) => request._id !== requesterId),
      }));
      toast.success('Solicitud rechazada');
      return { success: true };
    } catch (error) {
      toast.error(error.response?.data?.message || 'No se pudo rechazar la solicitud');
      return { success: false };
    }
  },

  cancelFriendRequest: async (userId) => {
    try {
      await apiClient.delete(`/users/remove-friend/${userId}`);
      set((state) => ({
        sentRequests: state.sentRequests.filter((request) => request._id !== userId),
      }));
      toast.success('Solicitud cancelada');
      return { success: true };
    } catch (error) {
      toast.error(error.response?.data?.message || 'No se pudo cancelar la solicitud');
      return { success: false };
    }
  },

  respondFriendRequest: async (requesterId, action) => {
    try {
      if (action === 'accept') {
        await apiClient.post(`/users/accept-friend/${requesterId}`);
      } else {
        await apiClient.delete(`/users/remove-friend/${requesterId}`);
      }
      
      set((state) => {
        const updatedRequests = state.friendRequests.filter(r => r._id !== requesterId);
        const updatedFriends = action === 'accept'
          ? [...state.friends, ...state.friendRequests.filter(r => r._id === requesterId)]
          : state.friends;
        
        return {
          friendRequests: updatedRequests,
          friends: updatedFriends,
        };
      });
      
      toast.success(`Solicitud ${action === 'accept' ? 'aceptada' : 'rechazada'}`);
      return { success: true };
    } catch (error) {
      toast.error('Error al responder solicitud');
      return { success: false };
    }
  },

  removeFriend: async (friendId) => {
    try {
      await apiClient.delete(`/users/remove-friend/${friendId}`);
      set((state) => ({
        friends: state.friends.filter(f => f._id !== friendId),
        sentRequests: state.sentRequests.filter(r => r._id !== friendId),
        friendRequests: state.friendRequests.filter(r => r._id !== friendId),
      }));
      toast.success('Amigo eliminado');
      return { success: true };
    } catch (error) {
      toast.error('Error al eliminar amigo');
      return { success: false };
    }
  },

  setRelationshipType: async (userId, type) => {
    try {
      const response = await apiClient.post(`/users/friendship/set-type/${userId}`, { type });
      set((state) => ({
        friends: state.friends.map((friend) =>
          friend._id === userId ? { ...friend, relationshipType: response.data.type || type } : friend
        ),
      }));
      toast.success('Relacion actualizada');
      return { success: true };
    } catch (error) {
      toast.error(error.response?.data?.message || 'No se pudo actualizar la relacion');
      return { success: false };
    }
  },

  // =========================
  // NOTIFICATIONS ACTIONS
  // =========================

  fetchNotifications: async () => {
    // 🔴 GUARD: No llamar si no hay usuario autenticado
    const authState = useAuthStore.getState();
    if (!authState.isLoggedIn || !authState.currentUser) {
      return;
    }
    
    try {
      set({ isNotificationsLoading: true });
      const response = await apiClient.get('/notifications');
      const notifications = Array.isArray(response.data?.notifications)
        ? response.data.notifications
        : [];
      const unreadCount = typeof response.data?.unreadCount === 'number'
        ? response.data.unreadCount
        : notifications.filter((n) => !n.isRead).length;
      set({ notifications, unreadCount, isNotificationsLoading: false });
    } catch (error) {
      // 🔴 MANEJO SILENCIOSO: 404/401 es normal si no hay notificaciones o no está autenticado
      if (error.response?.status === 404 || error.response?.status === 401) {
        set({ notifications: [], unreadCount: 0, isNotificationsLoading: false });
        return;
      }
      set({ notifications: [], unreadCount: 0, isNotificationsLoading: false });
    }
  },

  addNotification: (notification) => {
    set((state) => ({
      notifications: [notification, ...state.notifications],
      unreadCount: state.unreadCount + (notification?.isRead ? 0 : 1),
    }));
  },

  markConversationNotificationsRead: (conversationId) => {
    if (!conversationId) return;
    set((state) => {
      const nextNotifications = (state.notifications || []).map((notification) => {
        if (
          notification?.type === 'message' &&
          notification?.entityId?.toString?.() === conversationId.toString() &&
          !notification.isRead
        ) {
          return { ...notification, isRead: true };
        }
        return notification;
      });

      const nextUnreadCount = nextNotifications.filter((n) => !n.isRead).length;
      return { notifications: nextNotifications, unreadCount: nextUnreadCount };
    });
  },

  markOneAsRead: async (notificationId) => {
    const notification = get().notifications.find((n) => n._id === notificationId);
    
    if (notification && !notification.isRead) {
      const previousState = {
        notifications: get().notifications,
        unreadCount: get().unreadCount,
      };

      set((state) => ({
        notifications: state.notifications.map((n) =>
          n._id === notificationId ? { ...n, isRead: true } : n
        ),
        unreadCount: Math.max(0, state.unreadCount - 1),
      }));
      
      try {
        const response = await apiClient.patch(`/notifications/${notificationId}`);
        const notifications = Array.isArray(response.data?.notifications)
          ? response.data.notifications
          : [];
        const unreadCount = typeof response.data?.unreadCount === 'number'
          ? response.data.unreadCount
          : notifications.filter((n) => !n.isRead).length;
        set({ notifications, unreadCount });
      } catch (error) {
        console.error('Error al marcar notificación como leída:', error);
        set(previousState);
      }
    }
  },

  markAllAsRead: async () => {
    const previousState = {
      notifications: get().notifications,
      unreadCount: get().unreadCount,
    };

    set((state) => ({
      notifications: state.notifications.map((n) => ({ ...n, isRead: true })),
      unreadCount: 0,
    }));
    
    try {
      const response = await apiClient.patch('/notifications/read-all');
      const notifications = Array.isArray(response.data?.notifications)
        ? response.data.notifications
        : [];
      const unreadCount = typeof response.data?.unreadCount === 'number'
        ? response.data.unreadCount
        : notifications.filter((n) => !n.isRead).length;
      set({ notifications, unreadCount });
    } catch (error) {
      toast.error('Error al marcar todas las notificaciones.');
      set(previousState);
    }
  },

  deleteNotification: async (notificationId) => {
    const originalNotifications = get().notifications;
    const notificationToDelete = originalNotifications.find(n => n._id === notificationId);

    set((state) => ({
      notifications: state.notifications.filter((n) => n._id !== notificationId),
      unreadCount: notificationToDelete && !notificationToDelete.isRead 
        ? Math.max(0, state.unreadCount - 1) 
        : state.unreadCount,
    }));

    try {
      await apiClient.delete(`/notifications/${notificationId}`);
    } catch (error) {
      toast.error('No se pudo eliminar la notificación.');
      set({ notifications: originalNotifications });
    }
  },

  // =========================
  // BLOCKED USERS ACTIONS
  // =========================

  fetchBlockedUsers: async () => {
    set({ isLoading: true });
    try {
      const response = await apiClient.get('/users/blocked');
      set({
        blockedUsers: response.data.blockedUsers || [],
        blockedByUsers: response.data.blockedByUsers || [],
        isLoading: false,
      });
    } catch (error) {
      console.error('Error al cargar usuarios bloqueados:', error);
      set({ isLoading: false });
    }
  },

  blockUser: async (userId) => {
    try {
      const response = await apiClient.post(`/users/block/${userId}`);
      set((state) => ({
        blockedUsers: [...state.blockedUsers, { _id: userId, ...response.data.blockedUser }],
      }));
      toast.success('Usuario bloqueado');
      return { success: true };
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error al bloquear usuario');
      return { success: false };
    }
  },

  unblockUser: async (userId) => {
    try {
      await apiClient.delete(`/users/unblock/${userId}`);
      set((state) => ({
        blockedUsers: state.blockedUsers.filter(u => u._id !== userId),
      }));
      toast.success('Usuario desbloqueado');
      return { success: true };
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error al desbloquear usuario');
      return { success: false };
    }
  },

  isUserBlocked: (userId) => {
    const state = get();
    return state.blockedUsers.some(u => u._id === userId || u._id.toString() === userId.toString());
  },

  amIBlockedBy: (userId) => {
    const state = get();
    return state.blockedByUsers.some(u => u._id === userId || u._id.toString() === userId.toString());
  },

  handleBlockEvent: (action, blockerId) => {
    set((state) => {
      if (action === 'blocked') {
        return {
          blockedByUsers: [...state.blockedByUsers, { _id: blockerId }],
        };
      } else if (action === 'unblocked') {
        return {
          blockedByUsers: state.blockedByUsers.filter(u => u._id !== blockerId),
        };
      }
      return state;
    });
  },

  // =========================
  // COMPATIBILITY EXPORTS
  // =========================
  
  reset: () => set(initialState),
}));

// Compatibility wrapper exports (for backward compatibility)
export const useFriendsStore = useInteractionStore;
export const useNotificationsStore = useInteractionStore;
export const useBlockStore = useInteractionStore;
export const useActivityStore = useInteractionStore;
export const useSupportStore = useInteractionStore;
